import { useState, useEffect } from 'react'

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onNextPage: () => void
  onPreviousPage: () => void
}

export default function PaginationControls({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onNextPage,
  onPreviousPage
}: PaginationControlsProps) {
  const [pageInput, setPageInput] = useState(currentPage.toString())

  // Update input when currentPage changes externally
  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value)
  }

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const pageNumber = parseInt(pageInput, 10)
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber)
    } else {
      // Reset to current page if invalid
      setPageInput(currentPage.toString())
    }
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange(parseInt(e.target.value, 10))
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 7

    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      // Calculate range around current page
      let startPage = Math.max(2, currentPage - 2)
      let endPage = Math.min(totalPages - 1, currentPage + 2)

      // Adjust range if at the beginning
      if (currentPage <= 3) {
        endPage = Math.min(totalPages - 1, 5)
      }

      // Adjust range if at the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 4)
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('...')
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('...')
      }

      // Always show last page
      pages.push(totalPages)
    }

    return pages
  }

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalItems)

  return (
    <div className="pagination-controls">
      <div className="pagination-info">
        <label>Results per page:</label>
        <select
          value={pageSize}
          onChange={handlePageSizeChange}
          className="page-size-select"
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
        <span className="results-count">
          Showing {startItem}-{endItem} of {totalItems}
        </span>
      </div>

      <div className="pagination-navigation">
        <button
          onClick={onPreviousPage}
          disabled={currentPage === 1}
          className="pagination-btn"
          title="Previous page"
        >
          ‹
        </button>

        <div className="page-numbers">
          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="page-ellipsis">
                  ...
                </span>
              )
            }
            return (
              <button
                key={page}
                onClick={() => onPageChange(page as number)}
                className={`page-number-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            )
          })}
        </div>

        <button
          onClick={onNextPage}
          disabled={currentPage === totalPages}
          className="pagination-btn"
          title="Next page"
        >
          ›
        </button>

        <form onSubmit={handlePageInputSubmit} className="page-jump">
          <label>Go to page:</label>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={pageInput}
            onChange={handlePageInputChange}
            className="page-input"
          />
          <button type="submit" className="page-jump-btn">
            Go
          </button>
        </form>
      </div>
    </div>
  )
}
